import { NavigateFunction } from "react-router-dom";
import { TReactSetState, TUseTranslationTfn } from "../../data/AppType";
import {
  decryptData,
  handleError,
  toastError,
  toastSuccess,
} from "../../utils/AppFunctions";
import { routeAdmin, routeMaster } from "../../routes/AuthRoutePath";
import { setSessionToken } from "../../utils/AuthTokenStore";
import { routePublic } from "../../routes/PublicRoutePath";
import {
  RESEND_OTP,
  SELECT_LOGIN_SESSION,
  VERIFY_FORGOT_OTP,
  VERIFY_OTP,
} from "../../services/Endpoints";
import {
  DEFAULT_STATUS_CODE_CONFLICT,
  DEFAULT_STATUS_CODE_SUCCESS,
  LOGIN_SESSION_LIMIT_REACHED,
  LOGIN_SESSION_TICKET_EXPIRED,
  MESSAGE_LOCAL_UNKNOWN_ERROR_OCCURRED,
} from "../../utils/AppConstants";
import {
  BIT_VALUE,
  LOGIN_CLIENT_TYPE,
  SUBMIT_LOGIN_SESSION_SELECT,
} from "../../utils/AppEnumerations";

interface IOtpForm {
  username: string;
  otp?: string;
}

export interface ILoginSessionItem {
  id: string;
  client_type: LOGIN_CLIENT_TYPE;
  browser: string;
  os: string;
  ip_address: string;
  created_date: string;
  last_used_date: string;
}

export interface ILoginSessionLimit {
  login_ticket: string;
  max_sessions: number;
  required_count: number;
  sessions: ILoginSessionItem[];
}

export interface ILoginSessionSelectPayload {
  login_ticket: string;
  session_ids: string[];
}

const storeLoginTokens = (encryptedTokens: string) => {
  const token = JSON.parse(decryptData(encryptedTokens));
  setSessionToken(token.token);
};

export const submitLoginSessionSelect = async (
  values: ILoginSessionSelectPayload,
  navigation: NavigateFunction,
  setLoading: TReactSetState<boolean>,
  t: TUseTranslationTfn
): Promise<SUBMIT_LOGIN_SESSION_SELECT> => {
  setLoading(true);
  try {
    const result = await SELECT_LOGIN_SESSION(values);
    if (result?.code === DEFAULT_STATUS_CODE_SUCCESS) {
      storeLoginTokens(result.data);
      toastSuccess(t(result.message));
      navigation(`/${routeMaster.admin}/${routeAdmin.dashboard}`);
      return SUBMIT_LOGIN_SESSION_SELECT.Success;
    }
    toastError(t(result?.message || MESSAGE_LOCAL_UNKNOWN_ERROR_OCCURRED));
    return result?.data?.data?.event_key === LOGIN_SESSION_TICKET_EXPIRED
      ? SUBMIT_LOGIN_SESSION_SELECT.Expired
      : SUBMIT_LOGIN_SESSION_SELECT.Retry;
  } catch (error: any) {
    handleError(error, "submitLoginSessionSelect");
    return SUBMIT_LOGIN_SESSION_SELECT.Retry;
  } finally {
    setLoading(false);
  }
};

export const initialValuesOtp = {
  username: "",
  otp: "",
};

export const submitOtpApi = async (
  values: IOtpForm,
  navigation: NavigateFunction,
  setLoading: TReactSetState<boolean>,
  t: TUseTranslationTfn,
  isForgot: string,
  onSessionLimit: (sessionLimit: ILoginSessionLimit) => void
) => {
  setLoading(true);
  try {
    const { username, otp } = values;
    const payload = {
      username: username,
      otp: otp,
    };

    if (isForgot === BIT_VALUE.One) {
      const forgotOtpVerify = await VERIFY_FORGOT_OTP(payload);
      if (
        forgotOtpVerify &&
        forgotOtpVerify.code === DEFAULT_STATUS_CODE_SUCCESS
      ) {
        navigation(`/${routePublic.resetPassword}?at=${forgotOtpVerify.data}`);
      } else {
        toastError(
          t(forgotOtpVerify.message || MESSAGE_LOCAL_UNKNOWN_ERROR_OCCURRED)
        );
      }
    } else {
      const otpVerify = await VERIFY_OTP(payload);

      if (
        otpVerify?.code === DEFAULT_STATUS_CODE_CONFLICT &&
        otpVerify.data?.data?.event_key === LOGIN_SESSION_LIMIT_REACHED &&
        otpVerify.data.data.payload
      ) {
        onSessionLimit(JSON.parse(decryptData(otpVerify.data.data.payload)));
      } else if (otpVerify?.code === DEFAULT_STATUS_CODE_SUCCESS) {
        storeLoginTokens(otpVerify.data);
        toastSuccess(t(otpVerify.message));
        navigation(`/${routeMaster.admin}/${routeAdmin.dashboard}`);
      } else {
        toastError(
          t(otpVerify.message || MESSAGE_LOCAL_UNKNOWN_ERROR_OCCURRED)
        );
      }
    }
  } catch (error: any) {
    handleError(error, "submitOtpApi");
  } finally {
    setLoading(false);
  }
};
export const resendOtpApi = async (values: IOtpForm, t: TUseTranslationTfn) => {
  try {
    const { username, otp } = values;
    const payload = {
      username: username,
      otp: otp,
    };

    const forgotOtpVerify = await RESEND_OTP(payload);
    if (
      forgotOtpVerify &&
      forgotOtpVerify.code === DEFAULT_STATUS_CODE_SUCCESS
    ) {
      toastSuccess(t(forgotOtpVerify.message));
    } else {
      toastError(
        t(forgotOtpVerify.message || MESSAGE_LOCAL_UNKNOWN_ERROR_OCCURRED)
      );
    }
  } catch (error: any) {
    handleError(error, "resendOtpApi");
  }
};
